/* ============================================================
   policyDocs.jsx — formatted policy text: editor, reader, print

   Policies are stored as sanitized HTML (policies.body_html).
   - PolicyRichEditor: Word-style toolbar editor (TipTap) with "Import Word"
     (.docx -> HTML via mammoth, loaded on demand).
   - PolicyBody:       renders stored HTML for reading in the app.
   - printPolicy:      opens a clean, paper-styled copy and the browser's
                       print dialog (choose "Save as PDF" for a PDF).
   ============================================================ */
import React, { useEffect, useRef, useState } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Table from "@tiptap/extension-table";
import TableRow from "@tiptap/extension-table-row";
import TableCell from "@tiptap/extension-table-cell";
import TableHeader from "@tiptap/extension-table-header";
import DOMPurify from "dompurify";

/* ---------- sanitizing ---------- */
const ALLOWED_TAGS = [
  "p", "br", "strong", "b", "em", "i", "u", "s",
  "h1", "h2", "h3", "h4", "ul", "ol", "li", "blockquote", "hr",
  "table", "thead", "tbody", "tr", "th", "td", "colgroup", "col", "a",
];
const ALLOWED_ATTR = ["colspan", "rowspan", "href", "target", "rel"];

export function cleanPolicyHtml(html) {
  return DOMPurify.sanitize(html || "", { ALLOWED_TAGS, ALLOWED_ATTR });
}

/* Light cleanup for Word imports: drop empty paragraphs and images,
   un-bold headings that Word wrapped entirely in bold. */
function tidyImportedHtml(html) {
  let h = html
    .replace(/<img[^>]*>/gi, "")
    .replace(/<p>\s*(<br\s*\/?>)?\s*<\/p>/gi, "")
    .replace(/<(h[1-4])>\s*<strong>([\s\S]*?)<\/strong>\s*<\/\1>/gi, "<$1>$2</$1>")
    .replace(/<a id="[^"]*"><\/a>/gi, "");
  return cleanPolicyHtml(h);
}

/* ---------- reader ---------- */
export function PolicyBody({ html }) {
  const safe = React.useMemo(() => cleanPolicyHtml(html), [html]);
  if (!safe.trim()) return <div className="dim">No policy text yet.</div>;
  return <div className="policy-doc" dangerouslySetInnerHTML={{ __html: safe }} />;
}

/* ---------- editor ---------- */
function TB({ on, active, title, children, disabled }) {
  return (
    <button
      type="button"
      title={title}
      disabled={disabled}
      className={"pe-btn" + (active ? " on" : "")}
      onMouseDown={(e) => e.preventDefault()} // keep editor selection
      onClick={on}
    >
      {children}
    </button>
  );
}

export function PolicyRichEditor({ value, onChange }) {
  const [importing, setImporting] = useState(false);
  const [msg, setMsg] = useState("");
  const fileRef = useRef(null);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [2, 3, 4] } }),
      Underline,
      Table.configure({ resizable: false }),
      TableRow,
      TableHeader,
      TableCell,
    ],
    content: cleanPolicyHtml(value),
    onUpdate: ({ editor }) => onChange(editor.isEmpty ? "" : editor.getHTML()),
  });

  useEffect(() => () => editor?.destroy(), [editor]);

  const importWord = async (file) => {
    if (!file || !editor) return;
    if (!/\.docx$/i.test(file.name)) { setMsg("Please choose a Word .docx file."); return; }
    if (!editor.isEmpty && !window.confirm("Replace the current text with this Word document?")) return;
    setImporting(true);
    setMsg("");
    try {
      const mod = await import("mammoth/mammoth.browser");
      const mammoth = mod.default || mod;
      const arrayBuffer = await file.arrayBuffer();
      const res = await mammoth.convertToHtml(
        { arrayBuffer },
        { styleMap: ["p[style-name='Title'] => h2:fresh", "p[style-name='Subtitle'] => p:fresh"] }
      );
      const html = tidyImportedHtml(res.value);
      editor.commands.setContent(html, true);
      onChange(editor.getHTML());
      setMsg(`Imported "${file.name}". Check the formatting below — colored boxes and fonts from Word don't carry over.`);
    } catch (e) {
      setMsg(`Could not read that file: ${e.message}`);
    } finally {
      setImporting(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  if (!editor) return null;
  const inTable = editor.isActive("table");
  const c = () => editor.chain().focus();

  return (
    <div className="pe-wrap">
      <div className="pe-toolbar">
        <TB title="Import a Word document" on={() => fileRef.current?.click()} disabled={importing}>
          {importing ? "Importing…" : "⤒ Import Word"}
        </TB>
        <input ref={fileRef} type="file" accept=".docx" hidden onChange={(e) => importWord(e.target.files?.[0])} />
        <span className="pe-sep" />
        <TB title="Normal text" active={editor.isActive("paragraph")} on={() => c().setParagraph().run()}>Text</TB>
        <TB title="Heading" active={editor.isActive("heading", { level: 2 })} on={() => c().toggleHeading({ level: 2 }).run()}>H1</TB>
        <TB title="Subheading" active={editor.isActive("heading", { level: 3 })} on={() => c().toggleHeading({ level: 3 }).run()}>H2</TB>
        <TB title="Minor heading" active={editor.isActive("heading", { level: 4 })} on={() => c().toggleHeading({ level: 4 }).run()}>H3</TB>
        <span className="pe-sep" />
        <TB title="Bold" active={editor.isActive("bold")} on={() => c().toggleBold().run()}><b>B</b></TB>
        <TB title="Italic" active={editor.isActive("italic")} on={() => c().toggleItalic().run()}><i>I</i></TB>
        <TB title="Underline" active={editor.isActive("underline")} on={() => c().toggleUnderline().run()}><u>U</u></TB>
        <span className="pe-sep" />
        <TB title="Bulleted list" active={editor.isActive("bulletList")} on={() => c().toggleBulletList().run()}>• List</TB>
        <TB title="Numbered list" active={editor.isActive("orderedList")} on={() => c().toggleOrderedList().run()}>1. List</TB>
        <TB title="Callout box" active={editor.isActive("blockquote")} on={() => c().toggleBlockquote().run()}>▍Box</TB>
        <TB title="Divider line" on={() => c().setHorizontalRule().run()}>― Line</TB>
        <span className="pe-sep" />
        {!inTable ? (
          <TB title="Insert table" on={() => c().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()}>⊞ Table</TB>
        ) : (
          <>
            <TB title="Add row below" on={() => c().addRowAfter().run()}>+ Row</TB>
            <TB title="Add column right" on={() => c().addColumnAfter().run()}>+ Col</TB>
            <TB title="Delete row" on={() => c().deleteRow().run()}>− Row</TB>
            <TB title="Delete column" on={() => c().deleteColumn().run()}>− Col</TB>
            <TB title="Delete table" on={() => c().deleteTable().run()}>✕ Table</TB>
          </>
        )}
        <span className="pe-sep" />
        <TB title="Undo" on={() => c().undo().run()} disabled={!editor.can().undo()}>↶</TB>
        <TB title="Redo" on={() => c().redo().run()} disabled={!editor.can().redo()}>↷</TB>
      </div>
      {msg && <div className="pe-msg">{msg}</div>}
      <EditorContent editor={editor} className="pe-content policy-doc" />
    </div>
  );
}

/* ---------- print / PDF ---------- */
const escHtml = (s) =>
  String(s ?? "").replace(/[&<>"']/g, (ch) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[ch]));

const fmtLong = (d) => {
  if (!d) return "";
  const dt = typeof d === "string" && /^\d{4}-\d{2}-\d{2}$/.test(d) ? new Date(d + "T12:00:00") : new Date(d);
  return isNaN(dt) ? "" : dt.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
};

const PRINT_CSS = `
  @page { size: letter; margin: 0.75in 0.8in 0.8in; }
  * { box-sizing: border-box; }
  body { font-family: Calibri, Carlito, "Segoe UI", Arial, sans-serif; color: #111; font-size: 10.5pt; line-height: 1.45; margin: 0; }
  .lh { display: flex; justify-content: space-between; align-items: flex-end; border-bottom: 2px solid #1f3864; padding-bottom: 8px; margin-bottom: 18px; }
  .lh .co { font-size: 9pt; letter-spacing: 1.5px; color: #595959; font-weight: 700; }
  .lh .co small { display: block; font-weight: 400; letter-spacing: 0.5px; margin-top: 2px; }
  .lh .meta { font-size: 8.5pt; color: #595959; text-align: right; }
  h1.title { font-size: 20pt; color: #1f3864; margin: 0 0 4px; }
  .sub { color: #595959; font-size: 9pt; margin-bottom: 18px; }
  h1, h2, h3, h4 { color: #1f3864; page-break-after: avoid; break-after: avoid; }
  h2 { font-size: 13pt; margin: 20px 0 6px; padding-bottom: 3px; border-bottom: 1px solid #c9d3e3; }
  h3 { font-size: 11pt; margin: 14px 0 4px; }
  h4 { font-size: 10.5pt; margin: 12px 0 4px; }
  p { margin: 0 0 8px; }
  ul, ol { margin: 0 0 8px; padding-left: 22px; }
  li { margin-bottom: 3px; }
  li > p { margin: 0; }
  table { border-collapse: collapse; width: 100%; margin: 8px 0 12px; font-size: 9.5pt; }
  tr { page-break-inside: avoid; break-inside: avoid; }
  thead { display: table-header-group; }
  th, td { border: 1px solid #b7c2d3; padding: 5px 7px; vertical-align: top; text-align: left; }
  th { background: #1f3864; color: #fff; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  th p, td p { margin: 0 0 3px; }
  blockquote { margin: 10px 0; padding: 10px 14px; border-left: 4px solid #1f3864; background: #eef2f8;
               -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  hr { border: 0; border-top: 1px solid #b7c2d3; margin: 18px 0; }
  .ack { margin-top: 28px; padding-top: 14px; border-top: 2px solid #1f3864; page-break-inside: avoid; break-inside: avoid; }
  .ack h2 { border: 0; margin-top: 0; }
  .sig { display: grid; grid-template-columns: 1.4fr 1.4fr 0.8fr; gap: 22px; margin-top: 30px; }
  .sig > div { font-size: 8.5pt; color: #444; }
  .sig .val { font-size: 11pt; color: #111; min-height: 30px; border-bottom: 1px solid #111; padding-bottom: 3px; margin-bottom: 4px;
              display: flex; align-items: flex-end; }
  .sig img { max-height: 46px; display: block; }
  .foot { margin-top: 26px; font-size: 8pt; color: #777; text-align: center; }
  .hint { font: 13px system-ui, sans-serif; background: #fff8d6; border: 1px solid #e8d48a; padding: 8px 12px; margin-bottom: 16px; border-radius: 6px; }
  @media print { .hint { display: none; } }
`;

/* signed: optional { name, signed_at, signature (data URL) | typed_name } —
   prints that employee's signature block instead of blank lines. */
export function buildPolicyPrintHtml(policy, { statement, signed } = {}) {
  const eff = fmtLong(policy.effective_date);
  const metaParts = [`Version ${policy.version || 1}`];
  if (eff) metaParts.push(`Effective ${eff}`);
  const body = cleanPolicyHtml(policy.body_html);

  let ack = "";
  if (policy.requires_acknowledgment) {
    let sig;
    if (signed) {
      const mark = signed.signature
        ? `<img src="${escHtml(signed.signature)}" alt="signature">`
        : `<span style="font-family: 'Brush Script MT', cursive; font-size: 16pt;">${escHtml(signed.typed_name || signed.name)}</span>`;
      sig = `<div class="sig">
          <div><div class="val">${escHtml(signed.name)}</div>Employee name</div>
          <div><div class="val">${mark}</div>Signature${signed.signature ? "" : " (typed)"}</div>
          <div><div class="val">${escHtml(fmtLong(signed.signed_at))}</div>Date</div>
        </div>
        <p style="font-size:8pt;color:#777;margin-top:10px">Signed electronically in LVMGP Training against version ${policy.version || 1}.</p>`;
    } else {
      sig = `<div class="sig"><div><div class="val"></div>Employee name (print)</div><div><div class="val"></div>Signature</div><div><div class="val"></div>Date</div></div>`;
    }
    ack = `<div class="ack"><h2>Acknowledgment</h2><p>${escHtml(statement || "")}</p>${sig}</div>`;
  }

  return `<!doctype html><html><head><meta charset="utf-8">
<title>${escHtml(policy.title)} — v${policy.version || 1}</title>
<style>${PRINT_CSS}</style></head><body>
<div class="hint">To save a PDF, choose <b>Save as PDF</b> as the printer in the print dialog.</div>
<div class="lh"><div class="co">LAS VEGAS MINI GRAND PRIX<small>1401 N Rainbow Blvd · Las Vegas, NV 89108</small></div>
<div class="meta">${escHtml(policy.category || "Company Policy")}<br>${escHtml(metaParts.join(" · "))}</div></div>
<h1 class="title">${escHtml(policy.title)}</h1>
<div class="sub">${escHtml(metaParts.join(" · "))}</div>
${body}
${ack}
<div class="foot">${escHtml(policy.title)} · ${escHtml(metaParts.join(" · "))} · Printed ${escHtml(fmtLong(new Date()))}</div>
</body></html>`;
}

export function printPolicy(policy, opts = {}) {
  const html = buildPolicyPrintHtml(policy, opts);
  const w = window.open("", "_blank");
  if (w) {
    w.document.open();
    w.document.write(html);
    w.document.close();
    // Let images (signature) and fonts settle, then open the dialog once.
    // onload doesn't always fire for document.write'd windows, so there is a
    // fallback timer; the flag stops both from opening a dialog.
    let fired = false;
    const go = () => {
      if (fired) return;
      fired = true;
      try { w.focus(); w.print(); } catch (e) { /* window closed */ }
    };
    w.onload = () => setTimeout(go, 150);
    setTimeout(go, 900);
    return;
  }
  // Pop-up blocked: print through a hidden iframe instead.
  const f = document.createElement("iframe");
  f.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0";
  document.body.appendChild(f);
  f.contentDocument.open();
  f.contentDocument.write(html);
  f.contentDocument.close();
  setTimeout(() => {
    f.contentWindow.focus();
    f.contentWindow.print();
    setTimeout(() => f.remove(), 2000);
  }, 400);
}
